import {
  Button,
  Dialog,
  Link,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useState } from 'react';
import {
  StyledDialogActions,
  StyledDialogContent,
  StyledDialogPaper,
} from '~/modules/matters/modals/shared';
import { ModalHeader } from '~/shared/components/ModalHeader';
import { FOOTER_CONTACT_URL } from '~/shared/constants';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  /** Optional matter title to surface in the confirmation copy. */
  matterTitle?: string | null;
  onConfirm: () => void | Promise<void>;
  isClosing?: boolean;
}

const CloseMatterModal = ({
  isOpen,
  onClose,
  matterTitle,
  onConfirm,
  isClosing = false,
}: Props) => {
  const theme = useTheme();
  const isFullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  // Local pending guard covers the gap before the parent's mutation hook
  // re-renders with isClosing=true, so a fast double-click or backdrop dismiss
  // can't double-fire or orphan an in-flight close.
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isBusy = isClosing || isSubmitting;

  const handleClose = () => {
    if (isBusy) {
      return;
    }
    onClose();
  };

  const handleConfirm = async () => {
    if (isBusy) {
      return;
    }
    setIsSubmitting(true);
    try {
      await onConfirm();
    } finally {
      setIsSubmitting(false);
    }
  };

  const trimmedTitle = matterTitle?.trim();
  const subtitle = trimmedTitle ? (
    <>
      Are you sure you want to close <strong>{trimmedTitle}</strong>?
    </>
  ) : (
    'Are you sure you want to close this matter?'
  );

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      maxWidth="xs"
      fullWidth
      fullScreen={isFullScreen}
      PaperComponent={StyledDialogPaper}
    >
      <ModalHeader
        title="Close matter?"
        subtitle={subtitle}
        onClose={handleClose}
      />
      <StyledDialogContent>
        <ContentStack>
          <BodyText variant="body2" color="text.secondary">
            Closed matters can be reopened later from the matters list.
          </BodyText>
          <BodyText variant="body2" color="text.secondary">
            If any retainer refunds are necessary, please{' '}
            <SupportLink
              href={FOOTER_CONTACT_URL}
              target="_blank"
              rel="noopener noreferrer"
              underline="hover"
            >
              contact support
            </SupportLink>
            .
          </BodyText>
        </ContentStack>
      </StyledDialogContent>
      <StyledDialogActions>
        <StyledConfirmButton
          onClick={handleConfirm}
          color="error"
          variant="contained"
          disabled={isBusy}
        >
          {isBusy ? 'Closing…' : 'Close matter'}
        </StyledConfirmButton>
      </StyledDialogActions>
    </Dialog>
  );
};

export default CloseMatterModal;

const ContentStack = styled(Stack)(({ theme }) => ({
  paddingTop: theme.spacing(3),
  overflow: 'visible',
  gap: theme.spacing(2),
}));

const BodyText = styled(Typography)(({ theme }) => ({
  lineHeight: 1.5,
  margin: 0,
  '& + &': {
    marginTop: theme.spacing(0.5),
  },
}));

const SupportLink = styled(Link)(({ theme }) => ({
  fontWeight: 600,
  color: theme.palette.amount.main,
  '&:hover': {
    color: theme.palette.amount.dark,
  },
}));

const StyledConfirmButton = styled(Button)(({ theme }) => ({
  minHeight: 44,
  paddingTop: theme.spacing(1.25),
  paddingBottom: theme.spacing(1.25),
  paddingLeft: theme.spacing(2.5),
  paddingRight: theme.spacing(2.5),
}));
